import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Container,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useFormik } from 'formik';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import arLocale from 'i18n-iso-countries/langs/ar.json';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createCompanyEmployeeFormFields } from '../../../forms/fields/formFields/createCompanyEmployee';
import { createCompanyEmployeeSchema } from '../../../forms/validationSchema/formSchema/createCompanyEmployee';
import { treasuryEmployeeSchema } from '../../../forms/validationSchema/formSchema/treasuryEmployee';
import { useAppDispatch } from '../../../redux/hooks';
import {
  createCompanyEmployee,
  fetchCompanyEmployee,
  updateCompanyEmployee,
} from '../../../redux/modules/b2bThriftPlan/employees/companyEmployee.actions';
import {
  companyEmployeeCreateLoadingSelector,
  companyEmployeeLoadingSelector,
  companyEmployeeUpdateLoadingSelector,
  companyEmployeeSelector,
} from '../../../redux/modules/b2bThriftPlan/employees/companyEmployee.selectors';
import { fetchCompanyGroups } from '../../../redux/modules/b2bThriftPlan/companyGroups/companyGroup.actions';
import {
  companyGroupSelector,
  companyGroupsLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/companyGroups/companyGroup.selectors';
import { fetchCompanyDepartments } from '../../../redux/modules/b2bThriftPlan/departments/companyDepartments.actions';
import {
  companyDepartmentsSelector,
  companyDepartmentsLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/departments/companyDepartments.selectors';
import {
  fetchEmployeeStatuses,
  fetchEmployeeMaritalStatuses,
} from '../../../redux/modules/b2bThriftPlan/employeeStatuses/employeeStatuses.actions';
import {
  employeeStatusesSelector,
  employeeStatusesLoadingSelector,
  employeeMaritalStatusesLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/employeeStatuses/employeeStatuses.selectors';
import { fetchKycTypeOptions } from '../../../redux/modules/user/user.actions';
import { kycTypeOptionsLoadingSelector, userSelector } from '../../../redux/modules/user/user.selectors';
import { EducationLevel } from '../../../constants/nafaz';
import { TREASURY_OWNER_FUNDING_SOURCES } from '../../../constants/treasury';
import { useCreateTreasuryEmployeeMutation } from '../../../redux/features/treasury/treasury.apiSlice';
import { TreasuryEmployeeFormMode } from '../../../types/treasury';
import { getTreasuryErrorMessage } from '../../../utils/treasury';

countries.registerLocale(enLocale);
countries.registerLocale(arLocale);

const countryInfo = countries.getNames('en', { select: 'official' });
const countryList = Object.entries(countryInfo).map(([value, label]) => ({ label, value }));
const educationLevels = EducationLevel.en;

const normalizeEducationLevelValue = (value?: string | null) => {
  if (!value) return '';
  const normalized = value.trim();
  const exactValue = educationLevels.find(
    ({ value: optionValue }) =>
      optionValue === normalized ||
      optionValue === normalized.toUpperCase() ||
      optionValue === normalized.toLowerCase(),
  );
  if (exactValue) return exactValue.value;
  const labelMatch = educationLevels.find(({ label }) => label.toLowerCase() === normalized.toLowerCase());
  return labelMatch?.value ?? '';
};

const normalizeFormValue = (value: any): any => {
  if (value === '' || value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return JSON.stringify(value);
  return value;
};

function getEmptyEmployeeFormValues(): Record<string, any> {
  return {
    [createCompanyEmployeeFormFields.company_group]: '',
    [createCompanyEmployeeFormFields.full_name_en]: '',
    [createCompanyEmployeeFormFields.full_name_ar]: '',
    [createCompanyEmployeeFormFields.email]: '',
    [createCompanyEmployeeFormFields.phone_number]: '',
    [createCompanyEmployeeFormFields.national_id]: '',
    [createCompanyEmployeeFormFields.national_id_expiry]: '',
    [createCompanyEmployeeFormFields.id_issue_date]: '',
    [createCompanyEmployeeFormFields.gender]: '',
    [createCompanyEmployeeFormFields.address]: '',
    [createCompanyEmployeeFormFields.date_of_birth]: '',
    [createCompanyEmployeeFormFields.company_employee_marital_status]: '',
    [createCompanyEmployeeFormFields.job_title]: '',
    [createCompanyEmployeeFormFields.department]: '',
    [createCompanyEmployeeFormFields.joining_date]: '',
    [createCompanyEmployeeFormFields.salary]: 0,
    [createCompanyEmployeeFormFields.employee_contribution_amount]: 0,
    [createCompanyEmployeeFormFields.company_employee_status_id]: 1,
    [createCompanyEmployeeFormFields.type]: '',
    [createCompanyEmployeeFormFields.family_name_en]: '',
    [createCompanyEmployeeFormFields.family_name_ar]: '',
    [createCompanyEmployeeFormFields.first_name_ar]: '',
    [createCompanyEmployeeFormFields.first_name_en]: '',
    [createCompanyEmployeeFormFields.father_name_ar]: '',
    [createCompanyEmployeeFormFields.assurance_level]: '',
    [createCompanyEmployeeFormFields.father_name_en]: '',
    [createCompanyEmployeeFormFields.grand_father_name_ar]: '',
    [createCompanyEmployeeFormFields.grand_father_name_en]: '',
    [createCompanyEmployeeFormFields.nationality_ar]: '',
    [createCompanyEmployeeFormFields.nationality_en]: '',
    [createCompanyEmployeeFormFields.issue_location_ar]: '',
    [createCompanyEmployeeFormFields.issue_location_en]: '',
    [createCompanyEmployeeFormFields.language]: '',
    [createCompanyEmployeeFormFields.iqama_expiry_date]: '',
    [createCompanyEmployeeFormFields.education_level]: '',
    [createCompanyEmployeeFormFields.country]: '',
    [createCompanyEmployeeFormFields.five_years_financial_sector]: false,
    [createCompanyEmployeeFormFields.five_years_financial_sector_description]: '',
    [createCompanyEmployeeFormFields.board_directors_member]: false,
    [createCompanyEmployeeFormFields.board_directors_member_description]: '',
    [createCompanyEmployeeFormFields.individual_with_high_profile_political_role]: false,
    [createCompanyEmployeeFormFields.individual_with_high_profile_political_role_description]: '',
    [createCompanyEmployeeFormFields.not_beneficial_owner]: false,
    [createCompanyEmployeeFormFields.not_beneficial_owner_description]: '',
  };
}

/**
 * `treasury` embeds this form as the treasury employee step: the group and department come from the treasury setup
 * (hidden here), the owner's funding sources are added, and it submits to the treasury route instead.
 */
export const CreateCompanyEmployee = ({ treasury }: { treasury?: TreasuryEmployeeFormMode } = {}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const {
    companyId: routeCompanyId,
    action,
    employeeId: employeeIdParam,
  } = useParams<{
    companyId: string;
    action: string;
    employeeId?: string;
  }>();
  const companyId = treasury ? String(treasury.companyId) : routeCompanyId;
  const [fundingSources, setFundingSources] = React.useState<string[]>([]);
  const [createTreasuryEmployee, { isLoading: isTreasurySaving }] = useCreateTreasuryEmployeeMutation();
  const isCreateLoading = useSelector(companyEmployeeCreateLoadingSelector);
  const isUpdateLoading = useSelector(companyEmployeeUpdateLoadingSelector);
  const isFetchLoading = useSelector(companyEmployeeLoadingSelector);
  const { selectedCompanyEmployee } = useSelector(companyEmployeeSelector);
  const { companyGroups } = useSelector(companyGroupSelector);
  const { companyDepartments } = useSelector(companyDepartmentsSelector);
  const { employeeStatuses, employeeMaritalStatuses } = useSelector(employeeStatusesSelector);
  const { kycTypeOptions } = useSelector(userSelector);
  const isCompanyGroupsLoading = useSelector(companyGroupsLoadingSelector);
  const isCompanyDepartmentsLoading = useSelector(companyDepartmentsLoadingSelector);
  const isEmployeeStatusesLoading = useSelector(employeeStatusesLoadingSelector);
  const isEmployeeMaritalStatusesLoading = useSelector(employeeMaritalStatusesLoadingSelector);
  const isKycTypeOptionsLoading = useSelector(kycTypeOptionsLoadingSelector);

  const isEdit = !treasury && action === 'edit';
  const employeeId = isEdit && employeeIdParam ? Number(employeeIdParam) : null;
  const [initialFormValues, setInitialFormValues] = React.useState<Record<string, any>>(getEmptyEmployeeFormValues());
  const buildFormValues = React.useCallback(
    (employee: any): Record<string, any> => {
      const values = getEmptyEmployeeFormValues();
      const kyc = employee?.userEmployeeKyc || {};

      const parseNumber = (value: any): number => {
        if (value === null || value === undefined || value === '') return 0;
        const num = Number(value);
        return Number.isNaN(num) ? 0 : num;
      };

      const parseBoolean = (value: any): boolean => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'number') return value === 1;
        if (typeof value === 'string') {
          const normalized = value.trim().toLowerCase();
          return normalized === 'yes' || normalized === 'true' || normalized === '1';
        }
        return false;
      };

      const resolvedGroupName =
        employee?.companyGroup?.name ||
        companyGroups.find((group) => group.id === employee?.company_group_id)?.name ||
        '';
      const resolvedDepartmentName =
        employee?.department?.name ||
        companyDepartments.find((dept) => dept.id === employee?.department_id)?.name ||
        '';

      let resolvedGender = employee?.gender || '';
      if (!resolvedGender && typeof kyc.genderId === 'number') {
        if (kyc.genderId === 1) {
          resolvedGender = 'male';
        } else if (kyc.genderId === 2) {
          resolvedGender = 'female';
        }
      }

      let resolvedTypeId: string | number = '';
      if (kyc.typeId) {
        resolvedTypeId = kyc.typeId;
      } else {
        const typeName = (employee as any)?.type || kyc.type;
        if (typeName) {
          const matchedType = kycTypeOptions.find((option) => option.name === typeName);
          resolvedTypeId = matchedType?.id ?? '';
        }
      }

      values[createCompanyEmployeeFormFields.company_group] = resolvedGroupName;
      values[createCompanyEmployeeFormFields.full_name_en] = employee?.full_name_en || kyc.fullNameEn || '';
      values[createCompanyEmployeeFormFields.full_name_ar] = employee?.full_name_ar || kyc.fullNameAr || '';
      values[createCompanyEmployeeFormFields.email] = employee?.email || kyc.email || '';
      values[createCompanyEmployeeFormFields.phone_number] = employee?.phone_number || kyc.phone || '';
      values[createCompanyEmployeeFormFields.national_id] = employee?.national_id || kyc.nationalId || '';
      values[createCompanyEmployeeFormFields.national_id_expiry] =
        employee?.national_id_expiry || kyc.idExpiryDate || '';
      values[createCompanyEmployeeFormFields.id_issue_date] = kyc.idIssueDate || '';
      values[createCompanyEmployeeFormFields.gender] = resolvedGender;
      values[createCompanyEmployeeFormFields.address] = employee?.address || kyc.address || '';
      values[createCompanyEmployeeFormFields.date_of_birth] = employee?.date_of_birth || kyc.birthDate || '';
      values[createCompanyEmployeeFormFields.company_employee_marital_status] =
        employee?.maritalStatus?.status || kyc.maritalStatus || '';
      values[createCompanyEmployeeFormFields.job_title] = employee?.job_title || kyc.jobTitle || '';
      values[createCompanyEmployeeFormFields.department] = resolvedDepartmentName;
      values[createCompanyEmployeeFormFields.joining_date] = employee?.joining_date || '';
      values[createCompanyEmployeeFormFields.salary] = parseNumber(employee?.salary);
      values[createCompanyEmployeeFormFields.employee_contribution_amount] = parseNumber(
        employee?.employee_contribution_amount,
      );
      values[createCompanyEmployeeFormFields.company_employee_status_id] =
        employee?.company_employee_status_id ??
        employee?.employeeStatus?.id ??
        values[createCompanyEmployeeFormFields.company_employee_status_id];
      values[createCompanyEmployeeFormFields.type] = resolvedTypeId;
      values[createCompanyEmployeeFormFields.family_name_en] = kyc.familyNameEn || '';
      values[createCompanyEmployeeFormFields.family_name_ar] = kyc.familyNameAr || '';
      values[createCompanyEmployeeFormFields.first_name_ar] = kyc.firstNameAr || '';
      values[createCompanyEmployeeFormFields.first_name_en] = kyc.firstNameEn || '';
      values[createCompanyEmployeeFormFields.father_name_ar] = kyc.fatherNameAr || '';
      values[createCompanyEmployeeFormFields.assurance_level] = kyc.assuranceLevel || '';
      values[createCompanyEmployeeFormFields.father_name_en] = kyc.fatherNameEn || '';
      values[createCompanyEmployeeFormFields.grand_father_name_ar] = kyc.grandFatherNameAr || '';
      values[createCompanyEmployeeFormFields.grand_father_name_en] = kyc.grandFatherNameEn || '';
      values[createCompanyEmployeeFormFields.nationality_ar] = kyc.nationalityAr || '';
      values[createCompanyEmployeeFormFields.nationality_en] = kyc.nationalityEn || '';
      values[createCompanyEmployeeFormFields.issue_location_ar] = kyc.issueLocationAr || '';
      values[createCompanyEmployeeFormFields.issue_location_en] = kyc.issueLocationEn || '';
      values[createCompanyEmployeeFormFields.language] = kyc.language || '';
      values[createCompanyEmployeeFormFields.iqama_expiry_date] = kyc.iqamaExpiryDate || '';
      values[createCompanyEmployeeFormFields.education_level] = normalizeEducationLevelValue(
        kyc.educationLevel ?? (employee as any)?.education_level,
      );
      values[createCompanyEmployeeFormFields.country] = kyc.country || employee?.country || '';
      values[createCompanyEmployeeFormFields.five_years_financial_sector] = parseBoolean(
        kyc.fiveYearsFinancialSector ?? (employee as any)?.five_years_financial_sector,
      );
      values[createCompanyEmployeeFormFields.five_years_financial_sector_description] =
        kyc.fiveYearsFinancialSectorDescription || '';
      values[createCompanyEmployeeFormFields.board_directors_member] = parseBoolean(
        kyc.boardDirectorsMember ?? (employee as any)?.board_directors_member,
      );
      values[createCompanyEmployeeFormFields.board_directors_member_description] =
        kyc.boardDirectorsMemberDescription || '';
      values[createCompanyEmployeeFormFields.individual_with_high_profile_political_role] = parseBoolean(
        kyc.individualWithHighProfilePoliticalRole ?? (employee as any)?.individual_with_high_profile_political_role,
      );
      values[createCompanyEmployeeFormFields.individual_with_high_profile_political_role_description] =
        kyc.individualWithHighProfilePoliticalRoleDescription || '';
      values[createCompanyEmployeeFormFields.not_beneficial_owner] = parseBoolean(
        kyc.notBeneficialOwner ?? (employee as any)?.not_beneficial_owner,
      );
      values[createCompanyEmployeeFormFields.not_beneficial_owner_description] =
        kyc.notBeneficialOwnerDescription || '';

      return values;
    },
    [companyGroups, companyDepartments, kycTypeOptions],
  );

  const activeCompanyGroups = companyGroups.filter((group) => !group.isDeleted);
  const activeCompanyDepartments = companyDepartments.filter((department) => !department.isDeleted);
  const companyGroupOptions = React.useMemo(() => {
    const options = [...activeCompanyGroups];
    const currentGroupName = initialFormValues[createCompanyEmployeeFormFields.company_group];
    if (
      isEdit &&
      currentGroupName &&
      !options.some((group) => group.name === currentGroupName) &&
      selectedCompanyEmployee?.companyGroup
    ) {
      options.push(selectedCompanyEmployee.companyGroup);
    }
    return options;
  }, [
    activeCompanyGroups,
    initialFormValues[createCompanyEmployeeFormFields.company_group],
    isEdit,
    selectedCompanyEmployee,
  ]);

  // Fetch company groups, departments, and statuses on mount
  React.useEffect(() => {
    if (companyId) {
      dispatch(fetchCompanyGroups({ companyId: Number(companyId) }));
      dispatch(fetchCompanyDepartments({ companyId: Number(companyId) }));
    }
    // Fetch employee statuses and marital statuses (not company-specific)
    dispatch(fetchEmployeeStatuses());
    dispatch(fetchEmployeeMaritalStatuses());
    // Fetch KYC type options for the type dropdown
    dispatch(fetchKycTypeOptions());
  }, [dispatch, companyId]);

  // Fetch employee data when in edit mode
  React.useEffect(() => {
    if (isEdit && employeeId && companyId) {
      dispatch(fetchCompanyEmployee({ companyId: Number(companyId), employeeId }));
    }
  }, [isEdit, employeeId, companyId, dispatch]);

  // Store initial values when employee data is loaded
  React.useEffect(() => {
    if (isEdit && selectedCompanyEmployee) {
      setInitialFormValues(buildFormValues(selectedCompanyEmployee));
    } else if (!isEdit) {
      setInitialFormValues({
        ...getEmptyEmployeeFormValues(),
        // Treasury: the setup's group and department (thriftplan ignores them and uses the setup anyway), and the
        // hidden fields that mean nothing for the owner — joining date (thriftplan always writes today) and the
        // employee contribution (a treasury contribution is 0% employee). The status keeps its default: thriftplan
        // always starts an onboarded employee as pending.
        ...(treasury
          ? {
              [createCompanyEmployeeFormFields.company_group]: treasury.groupName,
              [createCompanyEmployeeFormFields.department]: treasury.departmentName,
              [createCompanyEmployeeFormFields.joining_date]: new Date().toISOString().split('T')[0],
              [createCompanyEmployeeFormFields.employee_contribution_amount]: 0,
              [createCompanyEmployeeFormFields.salary]: 0,
            }
          : {}),
      });
    }
  }, [
    isEdit,
    selectedCompanyEmployee,
    buildFormValues,
    companyGroups,
    companyDepartments,
    kycTypeOptions,
    treasury?.groupName,
    treasury?.departmentName,
  ]);

  const { handleSubmit, values, handleChange, touched, errors, setFieldValue, setFieldTouched, isValid, dirty } =
    useFormik({
      initialValues: initialFormValues,
      validationSchema: (() => {
        if (isEdit) return undefined;
        return treasury ? treasuryEmployeeSchema : createCompanyEmployeeSchema;
      })(),
      enableReinitialize: true,
      validateOnMount: !isEdit,
      validateOnBlur: !isEdit,
      validateOnChange: !isEdit,
      onSubmit: async (formValues) => {
        if (!companyId) {
          enqueueSnackbar('Company ID is required', { variant: 'error' });
          return;
        }

        try {
          if (isEdit && employeeId) {
            // Get only changed fields for update
            const changedFields: Record<string, any> = {};

            // Compare each field and add to changedFields if different
            Object.keys(formValues).forEach((key) => {
              const currentValue = formValues[key];
              const initialValue = initialFormValues[key];

              // Normalize for comparison
              const normalizedCurrent = normalizeFormValue(currentValue);
              const normalizedInitial = normalizeFormValue(initialValue);

              // Check if value changed
              if (normalizedCurrent !== normalizedInitial) {
                // Handle special field conversions
                if (key === createCompanyEmployeeFormFields.type) {
                  // Convert type id to name
                  const typeName = kycTypeOptions.find((option) => option.id === currentValue)?.name || '';
                  if (typeName) {
                    changedFields.type = typeName;
                  }
                } else if (key === createCompanyEmployeeFormFields.five_years_financial_sector) {
                  changedFields.five_years_financial_sector = (currentValue as boolean) ? 'Yes' : 'No';
                } else if (key === createCompanyEmployeeFormFields.board_directors_member) {
                  changedFields.board_directors_member = (currentValue as boolean) ? 'Yes' : 'No';
                } else if (key === createCompanyEmployeeFormFields.individual_with_high_profile_political_role) {
                  changedFields.individual_with_high_profile_political_role = (currentValue as boolean) ? 'Yes' : 'No';
                } else if (key === createCompanyEmployeeFormFields.not_beneficial_owner) {
                  changedFields.not_beneficial_owner = (currentValue as boolean) ? 'Yes' : 'No';
                } else {
                  // Map form field names to API field names
                  const fieldMapping: Record<string, string> = {
                    [createCompanyEmployeeFormFields.company_group]: 'company_group',
                    [createCompanyEmployeeFormFields.full_name_en]: 'full_name_en',
                    [createCompanyEmployeeFormFields.full_name_ar]: 'full_name_ar',
                    [createCompanyEmployeeFormFields.email]: 'email',
                    [createCompanyEmployeeFormFields.phone_number]: 'phone_number',
                    [createCompanyEmployeeFormFields.national_id]: 'national_id',
                    [createCompanyEmployeeFormFields.national_id_expiry]: 'national_id_expiry',
                    [createCompanyEmployeeFormFields.id_issue_date]: 'id_issue_date',
                    [createCompanyEmployeeFormFields.gender]: 'gender',
                    [createCompanyEmployeeFormFields.address]: 'address',
                    [createCompanyEmployeeFormFields.date_of_birth]: 'date_of_birth',
                    [createCompanyEmployeeFormFields.company_employee_marital_status]:
                      'company_employee_marital_status',
                    [createCompanyEmployeeFormFields.job_title]: 'job_title',
                    [createCompanyEmployeeFormFields.department]: 'department',
                    [createCompanyEmployeeFormFields.joining_date]: 'joining_date',
                    [createCompanyEmployeeFormFields.salary]: 'salary',
                    [createCompanyEmployeeFormFields.employee_contribution_amount]: 'employee_contribution_amount',
                    [createCompanyEmployeeFormFields.company_employee_status_id]: 'company_employee_status_id',
                    [createCompanyEmployeeFormFields.family_name_en]: 'family_name_en',
                    [createCompanyEmployeeFormFields.family_name_ar]: 'family_name_ar',
                    [createCompanyEmployeeFormFields.first_name_ar]: 'first_name_ar',
                    [createCompanyEmployeeFormFields.first_name_en]: 'first_name_en',
                    [createCompanyEmployeeFormFields.father_name_ar]: 'father_name_ar',
                    [createCompanyEmployeeFormFields.assurance_level]: 'assurance_level',
                    [createCompanyEmployeeFormFields.father_name_en]: 'father_name_en',
                    [createCompanyEmployeeFormFields.grand_father_name_ar]: 'grand_father_name_ar',
                    [createCompanyEmployeeFormFields.grand_father_name_en]: 'grand_father_name_en',
                    [createCompanyEmployeeFormFields.nationality_ar]: 'nationality_ar',
                    [createCompanyEmployeeFormFields.nationality_en]: 'nationality_en',
                    [createCompanyEmployeeFormFields.issue_location_ar]: 'issue_location_ar',
                    [createCompanyEmployeeFormFields.issue_location_en]: 'issue_location_en',
                    [createCompanyEmployeeFormFields.language]: 'language',
                    [createCompanyEmployeeFormFields.iqama_expiry_date]: 'iqama_expiry_date',
                    [createCompanyEmployeeFormFields.education_level]: 'education_level',
                    [createCompanyEmployeeFormFields.country]: 'country',
                    [createCompanyEmployeeFormFields.five_years_financial_sector_description]:
                      'five_years_financial_sector_description',
                    [createCompanyEmployeeFormFields.board_directors_member_description]:
                      'board_directors_member_description',
                    [createCompanyEmployeeFormFields.individual_with_high_profile_political_role_description]:
                      'individual_with_high_profile_political_role_description',
                    [createCompanyEmployeeFormFields.not_beneficial_owner_description]:
                      'not_beneficial_owner_description',
                  };

                  const apiFieldName = fieldMapping[key];
                  if (apiFieldName) {
                    changedFields[apiFieldName] = currentValue || '';
                  }
                }
              }
            });

            // Always include external_user_id and status in edit mode if they exist
            // if (selectedCompanyEmployee?.external_user_id !== undefined) {
            //   changedFields.external_user_id = selectedCompanyEmployee.external_user_id;
            // }
            // if (selectedCompanyEmployee?.employeeStatus?.status) {
            //   const status =
            //     selectedCompanyEmployee.employeeStatus.status.charAt(0).toUpperCase() +
            //     selectedCompanyEmployee.employeeStatus.status.slice(1);
            //   changedFields.status = status;
            // }

            await dispatch(
              updateCompanyEmployee({
                companyId: Number(companyId),
                employeeId,
                payload: changedFields as any,
              }),
            ).unwrap();
            enqueueSnackbar('Company employee updated successfully!', { variant: 'success' });
            navigate(`/companies/${companyId}/employees?tab=completed`);
            return;
          }

          // Create mode - send all fields
          const payload = {
            company_group: formValues[createCompanyEmployeeFormFields.company_group] || '',
            full_name_en: formValues[createCompanyEmployeeFormFields.full_name_en] || '',
            full_name_ar: formValues[createCompanyEmployeeFormFields.full_name_ar] || '',
            email: formValues[createCompanyEmployeeFormFields.email] || '',
            phone_number: formValues[createCompanyEmployeeFormFields.phone_number] || '',
            national_id: formValues[createCompanyEmployeeFormFields.national_id] || '',
            national_id_expiry: formValues[createCompanyEmployeeFormFields.national_id_expiry] || '',
            id_issue_date: formValues[createCompanyEmployeeFormFields.id_issue_date] || '',
            gender: formValues[createCompanyEmployeeFormFields.gender] || '',
            address: formValues[createCompanyEmployeeFormFields.address] || '',
            date_of_birth: formValues[createCompanyEmployeeFormFields.date_of_birth] || '',
            company_employee_marital_status:
              formValues[createCompanyEmployeeFormFields.company_employee_marital_status] || '',
            job_title: formValues[createCompanyEmployeeFormFields.job_title] || '',
            department: formValues[createCompanyEmployeeFormFields.department] || '',
            joining_date: formValues[createCompanyEmployeeFormFields.joining_date] || '',
            salary: formValues[createCompanyEmployeeFormFields.salary] || 0,
            employee_contribution_amount: formValues[createCompanyEmployeeFormFields.employee_contribution_amount] || 0,
            company_employee_status_id: formValues[createCompanyEmployeeFormFields.company_employee_status_id] || 1,
            type:
              kycTypeOptions.find((option) => option.id === formValues[createCompanyEmployeeFormFields.type])?.name ||
              '',
            family_name_en: formValues[createCompanyEmployeeFormFields.family_name_en] || '',
            family_name_ar: formValues[createCompanyEmployeeFormFields.family_name_ar] || '',
            first_name_ar: formValues[createCompanyEmployeeFormFields.first_name_ar] || '',
            first_name_en: formValues[createCompanyEmployeeFormFields.first_name_en] || '',
            father_name_ar: formValues[createCompanyEmployeeFormFields.father_name_ar] || '',
            assurance_level: formValues[createCompanyEmployeeFormFields.assurance_level] || '',
            father_name_en: formValues[createCompanyEmployeeFormFields.father_name_en] || '',
            grand_father_name_ar: formValues[createCompanyEmployeeFormFields.grand_father_name_ar] || '',
            grand_father_name_en: formValues[createCompanyEmployeeFormFields.grand_father_name_en] || '',
            nationality_ar: formValues[createCompanyEmployeeFormFields.nationality_ar] || '',
            nationality_en: formValues[createCompanyEmployeeFormFields.nationality_en] || '',
            issue_location_ar: formValues[createCompanyEmployeeFormFields.issue_location_ar] || '',
            issue_location_en: formValues[createCompanyEmployeeFormFields.issue_location_en] || '',
            language: formValues[createCompanyEmployeeFormFields.language] || '',
            iqama_expiry_date: formValues[createCompanyEmployeeFormFields.iqama_expiry_date] || '',
            education_level: formValues[createCompanyEmployeeFormFields.education_level] || '',
            country: formValues[createCompanyEmployeeFormFields.country] || '',
            five_years_financial_sector: (formValues[
              createCompanyEmployeeFormFields.five_years_financial_sector
            ] as boolean)
              ? 'Yes'
              : 'No',
            five_years_financial_sector_description:
              formValues[createCompanyEmployeeFormFields.five_years_financial_sector_description] || '',
            board_directors_member: (formValues[createCompanyEmployeeFormFields.board_directors_member] as boolean)
              ? 'Yes'
              : 'No',
            board_directors_member_description:
              formValues[createCompanyEmployeeFormFields.board_directors_member_description] || '',
            individual_with_high_profile_political_role: (formValues[
              createCompanyEmployeeFormFields.individual_with_high_profile_political_role
            ] as boolean)
              ? 'Yes'
              : 'No',
            individual_with_high_profile_political_role_description:
              formValues[createCompanyEmployeeFormFields.individual_with_high_profile_political_role_description] || '',
            not_beneficial_owner: (formValues[createCompanyEmployeeFormFields.not_beneficial_owner] as boolean)
              ? 'Yes'
              : 'No',
            not_beneficial_owner_description:
              formValues[createCompanyEmployeeFormFields.not_beneficial_owner_description] || '',
          };

          if (treasury) {
            try {
              await createTreasuryEmployee({
                companyId: treasury.companyId,
                payload: { ...payload, funding_source: fundingSources },
              }).unwrap();
              enqueueSnackbar('Treasury employee onboarding started!', { variant: 'success' });
              treasury.onDone();
            } catch (treasuryError) {
              enqueueSnackbar(getTreasuryErrorMessage(treasuryError, 'Company employee creation error!'), {
                variant: 'error',
              });
            }

            return;
          }

          await dispatch(
            createCompanyEmployee({
              companyId: Number(companyId),
              payload,
            }),
          ).unwrap();
          enqueueSnackbar('Company employee created!', { variant: 'success' });
          navigate(`/companies/${companyId}/employees`);
        } catch (error: any) {
          const errorMessage =
            error?.message || (isEdit ? 'Company employee update error!' : 'Company employee creation error!');
          enqueueSnackbar(errorMessage, { variant: 'error' });
        }
      },
    });

  const formHasChanged = React.useMemo(
    () =>
      Object.keys(values).some((key) => normalizeFormValue(values[key]) !== normalizeFormValue(initialFormValues[key])),
    [values, initialFormValues],
  );
  const isSubmitDisabled = React.useMemo(() => {
    if (isEdit) {
      return !formHasChanged;
    }
    if (treasury && fundingSources.length === 0) {
      return true;
    }
    return !dirty || !isValid;
  }, [isEdit, formHasChanged, dirty, isValid, treasury, fundingSources]);

  const formatDateForApi = (date: Date | null): string => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  return (
    <Container component="main" maxWidth="xl" disableGutters={Boolean(treasury)}>
      {!treasury && (
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">{isEdit ? 'Edit Company Employee' : 'Create Company Employee'}</Typography>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Basic Information Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: treasury ? 'none' : undefined }}>
            <FormControl fullWidth margin="normal" error={touched.company_group && !!errors.company_group}>
              <InputLabel id="company_group-label">Company Group *</InputLabel>
              <Select
                labelId="company_group-label"
                id="company_group"
                name={createCompanyEmployeeFormFields.company_group}
                value={values.company_group}
                label="Company Group *"
                onChange={handleChange}
                disabled={isCompanyGroupsLoading || companyGroupOptions.length === 0}
              >
                {companyGroupOptions.length === 0 ? (
                  <MenuItem disabled value="">
                    No company groups available
                  </MenuItem>
                ) : (
                  companyGroupOptions.map((group) => (
                    <MenuItem key={group.id} value={group.name} disabled={group.isDeleted}>
                      {group.name}
                      {group.isDeleted ? ' (inactive)' : ''}
                    </MenuItem>
                  ))
                )}
              </Select>
              {touched.company_group && typeof errors.company_group === 'string' && (
                <FormHelperText>{errors.company_group}</FormHelperText>
              )}
              {!isCompanyGroupsLoading && activeCompanyGroups.length === 0 && (
                <FormHelperText sx={{ color: 'warning.main' }}>
                  No company groups available. Please create a company group first.
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="full_name_en"
              name={createCompanyEmployeeFormFields.full_name_en}
              label="Full Name (EN) *"
              value={values.full_name_en}
              onChange={handleChange}
              error={touched.full_name_en && !!errors.full_name_en}
              helperText={touched.full_name_en && typeof errors.full_name_en === 'string' ? errors.full_name_en : ''}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="full_name_ar"
              name={createCompanyEmployeeFormFields.full_name_ar}
              label="Full Name (AR) *"
              value={values.full_name_ar}
              onChange={handleChange}
              error={touched.full_name_ar && !!errors.full_name_ar}
              helperText={touched.full_name_ar && typeof errors.full_name_ar === 'string' ? errors.full_name_ar : ''}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="email"
              name={createCompanyEmployeeFormFields.email}
              label="Email *"
              type="email"
              value={values.email}
              onChange={handleChange}
              error={touched.email && !!errors.email}
              helperText={touched.email && typeof errors.email === 'string' ? errors.email : ''}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="phone_number"
              name={createCompanyEmployeeFormFields.phone_number}
              label="Phone Number *"
              value={values.phone_number}
              onChange={handleChange}
              error={touched.phone_number && !!errors.phone_number}
              helperText={touched.phone_number && typeof errors.phone_number === 'string' ? errors.phone_number : ''}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="national_id"
              name={createCompanyEmployeeFormFields.national_id}
              label="National ID *"
              value={values.national_id}
              onChange={handleChange}
              error={touched.national_id && !!errors.national_id}
              helperText={touched.national_id && typeof errors.national_id === 'string' ? errors.national_id : ''}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DatePicker
              label="National ID Expiry *"
              value={values.national_id_expiry ? new Date(values.national_id_expiry) : null}
              onChange={(newValue) =>
                setFieldValue(createCompanyEmployeeFormFields.national_id_expiry, formatDateForApi(newValue))
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  error: touched.national_id_expiry && !!errors.national_id_expiry,
                  helperText:
                    touched.national_id_expiry && typeof errors.national_id_expiry === 'string'
                      ? errors.national_id_expiry
                      : '',
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DatePicker
              label="ID Issue Date"
              value={values.id_issue_date ? new Date(values.id_issue_date) : null}
              onChange={(newValue) =>
                setFieldValue(createCompanyEmployeeFormFields.id_issue_date, formatDateForApi(newValue))
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  error: touched.id_issue_date && !!errors.id_issue_date,
                  helperText:
                    touched.id_issue_date && typeof errors.id_issue_date === 'string' ? errors.id_issue_date : '',
                  required: true,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal" error={touched.gender && !!errors.gender}>
              <InputLabel id="gender-label">Gender *</InputLabel>
              <Select
                labelId="gender-label"
                id="gender"
                name={createCompanyEmployeeFormFields.gender}
                value={values.gender}
                label="Gender *"
                onChange={handleChange}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
              </Select>
              {touched.gender && typeof errors.gender === 'string' && <FormHelperText>{errors.gender}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <DatePicker
              label="Date of Birth *"
              value={values.date_of_birth ? new Date(values.date_of_birth) : null}
              onChange={(newValue) =>
                setFieldValue(createCompanyEmployeeFormFields.date_of_birth, formatDateForApi(newValue))
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  error: touched.date_of_birth && !!errors.date_of_birth,
                  helperText:
                    touched.date_of_birth && typeof errors.date_of_birth === 'string' ? errors.date_of_birth : '',
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="address"
              name={createCompanyEmployeeFormFields.address}
              label="Address *"
              multiline
              rows={3}
              value={values.address}
              onChange={handleChange}
              error={touched.address && !!errors.address}
              helperText={touched.address && typeof errors.address === 'string' ? errors.address : ''}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              margin="normal"
              error={touched.company_employee_marital_status && !!errors.company_employee_marital_status}
              disabled={isEmployeeMaritalStatusesLoading || employeeMaritalStatuses.length === 0}
            >
              <InputLabel id="marital_status-label">Marital Status *</InputLabel>
              <Select
                labelId="marital_status-label"
                id="marital_status"
                name={createCompanyEmployeeFormFields.company_employee_marital_status}
                value={values.company_employee_marital_status}
                label="Marital Status *"
                onChange={handleChange}
              >
                {employeeMaritalStatuses.length === 0 ? (
                  <MenuItem value="" disabled>
                    {isEmployeeMaritalStatusesLoading ? 'Loading...' : 'No marital statuses available'}
                  </MenuItem>
                ) : (
                  employeeMaritalStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.status}>
                      {status.status}
                    </MenuItem>
                  ))
                )}
              </Select>
              {touched.company_employee_marital_status &&
                typeof errors.company_employee_marital_status === 'string' && (
                  <FormHelperText>{errors.company_employee_marital_status}</FormHelperText>
                )}
              {employeeMaritalStatuses.length === 0 && !isEmployeeMaritalStatusesLoading && (
                <FormHelperText>No marital statuses available. Please contact support.</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Employment Information Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Employment Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="job_title"
              name={createCompanyEmployeeFormFields.job_title}
              label="Job Title *"
              value={values.job_title}
              onChange={handleChange}
              error={touched.job_title && !!errors.job_title}
              helperText={touched.job_title && typeof errors.job_title === 'string' ? errors.job_title : ''}
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: treasury ? 'none' : undefined }}>
            <FormControl fullWidth margin="normal" error={touched.department && !!errors.department}>
              <InputLabel id="department-label">Department *</InputLabel>
              <Select
                labelId="department-label"
                id="department"
                name={createCompanyEmployeeFormFields.department}
                value={values.department}
                label="Department *"
                onChange={handleChange}
                disabled={isCompanyDepartmentsLoading || activeCompanyDepartments.length === 0}
              >
                {activeCompanyDepartments.length === 0 ? (
                  <MenuItem disabled value="">
                    No departments available
                  </MenuItem>
                ) : (
                  activeCompanyDepartments.map((department) => (
                    <MenuItem key={department.id} value={department.name}>
                      {department.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {touched.department && typeof errors.department === 'string' && (
                <FormHelperText>{errors.department}</FormHelperText>
              )}
              {!isCompanyDepartmentsLoading && activeCompanyDepartments.length === 0 && (
                <FormHelperText sx={{ color: 'warning.main' }}>
                  No departments available. Please create a department first.
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: treasury ? 'none' : undefined }}>
            <DatePicker
              label="Joining Date *"
              value={values.joining_date ? new Date(values.joining_date) : null}
              onChange={(newValue) =>
                setFieldValue(createCompanyEmployeeFormFields.joining_date, formatDateForApi(newValue))
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  error: touched.joining_date && !!errors.joining_date,
                  helperText:
                    touched.joining_date && typeof errors.joining_date === 'string' ? errors.joining_date : '',
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: treasury ? 'none' : undefined }}>
            <TextField
              fullWidth
              margin="normal"
              id="salary"
              name={createCompanyEmployeeFormFields.salary}
              label="Salary *"
              type="number"
              value={values.salary}
              onChange={handleChange}
              error={touched.salary && !!errors.salary}
              helperText={touched.salary && typeof errors.salary === 'string' ? errors.salary : ''}
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: treasury ? 'none' : undefined }}>
            <TextField
              fullWidth
              margin="normal"
              id="employee_contribution_amount"
              name={createCompanyEmployeeFormFields.employee_contribution_amount}
              label="Employee Contribution Amount *"
              type="number"
              value={values.employee_contribution_amount}
              onChange={handleChange}
              error={touched.employee_contribution_amount && !!errors.employee_contribution_amount}
              helperText={
                touched.employee_contribution_amount && typeof errors.employee_contribution_amount === 'string'
                  ? errors.employee_contribution_amount
                  : ''
              }
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: treasury ? 'none' : undefined }}>
            <FormControl
              fullWidth
              margin="normal"
              error={touched.company_employee_status_id && !!errors.company_employee_status_id}
              disabled={isEmployeeStatusesLoading || employeeStatuses.length === 0}
            >
              <InputLabel id="employee_status-label">Employee Status *</InputLabel>
              <Select
                labelId="employee_status-label"
                id="employee_status"
                name={createCompanyEmployeeFormFields.company_employee_status_id}
                value={values.company_employee_status_id}
                label="Employee Status *"
                onChange={handleChange}
              >
                {employeeStatuses.length === 0 ? (
                  <MenuItem value="" disabled>
                    {isEmployeeStatusesLoading ? 'Loading...' : 'No statuses available'}
                  </MenuItem>
                ) : (
                  employeeStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.status}
                    </MenuItem>
                  ))
                )}
              </Select>
              {touched.company_employee_status_id && typeof errors.company_employee_status_id === 'string' && (
                <FormHelperText>{errors.company_employee_status_id}</FormHelperText>
              )}
              {employeeStatuses.length === 0 && !isEmployeeStatusesLoading && (
                <FormHelperText>No employee statuses available. Please contact support.</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* KYC Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              KYC
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="first_name_en"
              name={createCompanyEmployeeFormFields.first_name_en}
              label="First Name (EN)"
              value={values.first_name_en}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="first_name_ar"
              name={createCompanyEmployeeFormFields.first_name_ar}
              label="First Name (AR)"
              value={values.first_name_ar}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="family_name_en"
              name={createCompanyEmployeeFormFields.family_name_en}
              label="Family Name (EN)"
              value={values.family_name_en}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="family_name_ar"
              name={createCompanyEmployeeFormFields.family_name_ar}
              label="Family Name (AR)"
              value={values.family_name_ar}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="father_name_en"
              name={createCompanyEmployeeFormFields.father_name_en}
              label="Father Name (EN)"
              value={values.father_name_en}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="father_name_ar"
              name={createCompanyEmployeeFormFields.father_name_ar}
              label="Father Name (AR)"
              value={values.father_name_ar}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="grand_father_name_en"
              name={createCompanyEmployeeFormFields.grand_father_name_en}
              label="Grand Father Name (EN)"
              value={values.grand_father_name_en}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="grand_father_name_ar"
              name={createCompanyEmployeeFormFields.grand_father_name_ar}
              label="Grand Father Name (AR)"
              value={values.grand_father_name_ar}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="nationality_en"
              name={createCompanyEmployeeFormFields.nationality_en}
              label="Nationality (EN)"
              value={values.nationality_en}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="nationality_ar"
              name={createCompanyEmployeeFormFields.nationality_ar}
              label="Nationality (AR)"
              value={values.nationality_ar}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="issue_location_en"
              name={createCompanyEmployeeFormFields.issue_location_en}
              label="Issue Location (EN)"
              value={values.issue_location_en}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="issue_location_ar"
              name={createCompanyEmployeeFormFields.issue_location_ar}
              label="Issue Location (AR)"
              value={values.issue_location_ar}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              margin="normal"
              required
              error={touched.type && typeof errors.type === 'string' && !!errors.type}
            >
              <InputLabel id="type-label" required>
                Type
              </InputLabel>
              <Select
                labelId="type-label"
                id="type"
                name={createCompanyEmployeeFormFields.type}
                value={values.type || ''}
                label="Type"
                onChange={handleChange}
                disabled={isKycTypeOptionsLoading}
              >
                {kycTypeOptions.map(({ id: ktId, name }) => (
                  <MenuItem key={ktId} value={ktId}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
              {touched.type && typeof errors.type === 'string' && <FormHelperText>{errors.type}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              margin="normal"
              id="assurance_level"
              name={createCompanyEmployeeFormFields.assurance_level}
              label="Assurance Level"
              value={values.assurance_level}
              onChange={handleChange}
              error={touched.assurance_level && typeof errors.assurance_level === 'string' && !!errors.assurance_level}
              helperText={
                touched.assurance_level && typeof errors.assurance_level === 'string' ? errors.assurance_level : ''
              }
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal" required error={touched.language && !!errors.language}>
              <InputLabel id="language-label" required>
                Language
              </InputLabel>
              <Select
                labelId="language-label"
                id="language"
                name={createCompanyEmployeeFormFields.language}
                value={values.language}
                label="Language"
                onChange={handleChange}
                MenuProps={{ sx: { paper: { maxHeight: 200 } } }}
              >
                {[
                  { label: 'English', value: 'en' },
                  { label: 'Arabian', value: 'ar' },
                ].map(({ label, value }) => (
                  <MenuItem key={label} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              {touched.language && typeof errors.language === 'string' && (
                <FormHelperText>{errors.language}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <DatePicker
              label="Iqama Expiry Date"
              value={values.iqama_expiry_date ? new Date(values.iqama_expiry_date) : null}
              onChange={(newValue) => {
                setFieldValue(createCompanyEmployeeFormFields.iqama_expiry_date, formatDateForApi(newValue));
                // Mark field as touched for validation feedback
                setFieldTouched(createCompanyEmployeeFormFields.iqama_expiry_date, true, false);
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  error:
                    touched.iqama_expiry_date &&
                    typeof errors.iqama_expiry_date === 'string' &&
                    !!errors.iqama_expiry_date,
                  helperText:
                    touched.iqama_expiry_date && typeof errors.iqama_expiry_date === 'string'
                      ? errors.iqama_expiry_date
                      : '',
                  required: true,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              margin="normal"
              required
              error={touched.education_level && typeof errors.education_level === 'string' && !!errors.education_level}
            >
              <InputLabel id="education_level-label" required>
                Education Level
              </InputLabel>
              <Select
                labelId="education_level-label"
                id="education_level"
                name={createCompanyEmployeeFormFields.education_level}
                value={values.education_level || ''}
                label="Education Level"
                onChange={handleChange}
                MenuProps={{ sx: { paper: { maxHeight: 200 } } }}
              >
                {educationLevels.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              {touched.education_level && typeof errors.education_level === 'string' && (
                <FormHelperText>{errors.education_level}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              margin="normal"
              required
              error={touched.country && typeof errors.country === 'string' && !!errors.country}
            >
              <InputLabel id="country-label" required>
                Country
              </InputLabel>
              <Select
                labelId="country-label"
                id="country"
                name={createCompanyEmployeeFormFields.country}
                value={values.country}
                label="Country"
                onChange={handleChange}
                MenuProps={{ sx: { paper: { maxHeight: 200 } } }}
              >
                {countryList.map(({ label, value }) => (
                  <MenuItem key={label} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              {touched.country && typeof errors.country === 'string' && (
                <FormHelperText>{errors.country}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Compliance Questions */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Compliance Questions
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControlLabel
              sx={{ mt: 2 }}
              label="Five Years Financial Sector"
              labelPlacement="start"
              componentsProps={{
                typography: { textTransform: 'capitalize' },
              }}
              name={createCompanyEmployeeFormFields.five_years_financial_sector}
              id={createCompanyEmployeeFormFields.five_years_financial_sector}
              control={
                <Switch
                  name={createCompanyEmployeeFormFields.five_years_financial_sector}
                  checked={values.five_years_financial_sector}
                  onChange={handleChange}
                />
              }
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              margin="normal"
              id="five_years_financial_sector_description"
              name={createCompanyEmployeeFormFields.five_years_financial_sector_description}
              label="Five Years Financial Sector Description"
              multiline
              rows={2}
              disabled={!values.five_years_financial_sector}
              value={values.five_years_financial_sector_description}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControlLabel
              sx={{ mt: 2 }}
              label="Board Directors Member"
              labelPlacement="start"
              componentsProps={{
                typography: { textTransform: 'capitalize' },
              }}
              name={createCompanyEmployeeFormFields.board_directors_member}
              id={createCompanyEmployeeFormFields.board_directors_member}
              control={
                <Switch
                  name={createCompanyEmployeeFormFields.board_directors_member}
                  checked={values.board_directors_member}
                  onChange={handleChange}
                />
              }
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              margin="normal"
              id="board_directors_member_description"
              name={createCompanyEmployeeFormFields.board_directors_member_description}
              label="Board Directors Member Description"
              multiline
              rows={2}
              disabled={!values.board_directors_member}
              value={values.board_directors_member_description}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControlLabel
              sx={{ mt: 2 }}
              label="Individual With High Profile Political Role"
              labelPlacement="start"
              componentsProps={{
                typography: { textTransform: 'capitalize' },
              }}
              name={createCompanyEmployeeFormFields.individual_with_high_profile_political_role}
              id={createCompanyEmployeeFormFields.individual_with_high_profile_political_role}
              control={
                <Switch
                  name={createCompanyEmployeeFormFields.individual_with_high_profile_political_role}
                  checked={values.individual_with_high_profile_political_role}
                  onChange={handleChange}
                />
              }
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              margin="normal"
              id="individual_with_high_profile_political_role_description"
              name={createCompanyEmployeeFormFields.individual_with_high_profile_political_role_description}
              label="Individual With High Profile Political Role Description"
              multiline
              rows={2}
              disabled={!values.individual_with_high_profile_political_role}
              value={values.individual_with_high_profile_political_role_description}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControlLabel
              sx={{ mt: 2 }}
              label="Not Beneficial Owner"
              labelPlacement="start"
              componentsProps={{
                typography: { textTransform: 'capitalize' },
              }}
              name={createCompanyEmployeeFormFields.not_beneficial_owner}
              id={createCompanyEmployeeFormFields.not_beneficial_owner}
              control={
                <Switch
                  name={createCompanyEmployeeFormFields.not_beneficial_owner}
                  checked={values.not_beneficial_owner}
                  onChange={handleChange}
                />
              }
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              margin="normal"
              id="not_beneficial_owner_description"
              name={createCompanyEmployeeFormFields.not_beneficial_owner_description}
              label="Not Beneficial Owner Description"
              multiline
              rows={2}
              disabled={!values.not_beneficial_owner}
              value={values.not_beneficial_owner_description}
              onChange={handleChange}
            />
          </Grid>

          {/* Treasury: the owner's funding sources, for the company's Alpaca KYB */}
          {treasury && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" required error={fundingSources.length === 0}>
                <InputLabel id="funding_source-label">Funding Source</InputLabel>
                <Select
                  labelId="funding_source-label"
                  id="funding_source"
                  multiple
                  value={fundingSources}
                  label="Funding Source"
                  onChange={(event) => {
                    const { value } = event.target;
                    setFundingSources(typeof value === 'string' ? value.split(',') : value);
                  }}
                  renderValue={(selected) =>
                    (selected as string[])
                      .map((value) => TREASURY_OWNER_FUNDING_SOURCES.find((option) => option.value === value)?.label)
                      .join(', ')
                  }
                >
                  {TREASURY_OWNER_FUNDING_SOURCES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {fundingSources.length === 0 && <FormHelperText>Select at least one funding source</FormHelperText>}
              </FormControl>
            </Grid>
          )}

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loading={isCreateLoading || isUpdateLoading || isFetchLoading || isTreasurySaving}
                startIcon={<SaveIcon />}
                disabled={isSubmitDisabled || isCreateLoading || isUpdateLoading || isFetchLoading || isTreasurySaving}
              >
                {isEdit ? 'Update Employee' : 'Create Employee'}
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};
